/**
 * Pure grouping and subtotal logic for the Bank Balances page.
 *
 * Extracted from BankBalancesSection so it can be tested without
 * pulling in React component dependencies (Card, BankLogo, etc.).
 *
 * The reconciliation invariant: the sum of all per-group subtotals
 * must equal the grand total exactly, because both use the same
 * per-account parse+convert path.
 */

import { convertAmount } from './currencyConvert';

/** Parse a string balance to number. Returns null if missing. */
export function parseBalance(val) {
  if (val == null) return null;
  var n = Number(val);
  if (isNaN(n)) return null;
  return n;
}

/**
 * Compute the converted subtotal for a list of accounts.
 * Uses the same per-account parse+convert path as the grand total,
 * so the sum of all group subtotals equals the grand total exactly.
 */
export function computeGroupSubtotal(accounts, displayCurrency, fxRates) {
  var total = 0;
  for (var i = 0; i < accounts.length; i++) {
    var bal = parseBalance(accounts[i].balance);
    if (bal == null) continue;
    var c = convertAmount(bal, accounts[i].currency, displayCurrency, fxRates);
    total += c ?? 0;
  }
  return total;
}

/**
 * Group accounts by bank_name. Returns an array of { bankName, accounts, subtotal }
 * sorted by converted subtotal descending (biggest bank first).
 * Ties are broken alphabetically by bank name.
 */
export function groupAccountsByBank(accounts, displayCurrency, fxRates) {
  var map = {};
  for (var i = 0; i < accounts.length; i++) {
    var acc = accounts[i];
    var bank = acc.bank_name || 'Unknown';
    if (!map[bank]) {
      map[bank] = [];
    }
    map[bank].push(acc);
  }
  var groups = Object.keys(map).map(function (bankName) {
    return {
      bankName: bankName,
      accounts: map[bankName],
      subtotal: computeGroupSubtotal(map[bankName], displayCurrency, fxRates),
    };
  });
  groups.sort(function (a, b) {
    if (b.subtotal !== a.subtotal) return b.subtotal - a.subtotal;
    return a.bankName.localeCompare(b.bankName);
  });
  return groups;
}

/**
 * Group accounts by entity_id. Returns { entities, unassigned }.
 * Entities sorted alphabetically by name.
 */
export function groupAccountsByEntity(accounts) {
  var map = {};
  var unassigned = [];
  for (var i = 0; i < accounts.length; i++) {
    var acc = accounts[i];
    if (acc.entity_id === 'unassigned') {
      unassigned.push(acc);
    } else {
      if (!map[acc.entity_id]) {
        map[acc.entity_id] = { entityName: acc.entity_name, accounts: [] };
      }
      map[acc.entity_id].accounts.push(acc);
    }
  }
  var entities = Object.keys(map).sort(function (a, b) {
    return map[a].entityName.localeCompare(map[b].entityName);
  }).map(function (eid) {
    return { entityId: eid, entityName: map[eid].entityName, accounts: map[eid].accounts };
  });
  return { entities: entities, unassigned: unassigned };
}

/**
 * Two-level grouping: Entity > Bank > accounts.
 *
 * Returns an array of entity groups, each containing nested bank groups.
 * Entities sorted alphabetically by name; "Unassigned" appended last.
 * Banks within each entity sorted by subtotal descending (same as groupAccountsByBank).
 * Subtotals computed at both entity and bank levels using the same per-account
 * parse+convert path so the reconciliation invariant holds.
 */
export function groupAccountsByEntityThenBank(accounts, displayCurrency, fxRates) {
  var entityMap = {};
  var unassignedAccounts = [];

  for (var i = 0; i < accounts.length; i++) {
    var acc = accounts[i];
    if (acc.entity_id === 'unassigned') {
      unassignedAccounts.push(acc);
    } else {
      if (!entityMap[acc.entity_id]) {
        entityMap[acc.entity_id] = { entityName: acc.entity_name, accounts: [] };
      }
      entityMap[acc.entity_id].accounts.push(acc);
    }
  }

  var entityGroups = Object.keys(entityMap).map(function (eid) {
    var entry = entityMap[eid];
    return {
      entityId: eid,
      entityName: entry.entityName,
      subtotal: computeGroupSubtotal(entry.accounts, displayCurrency, fxRates),
      banks: groupAccountsByBank(entry.accounts, displayCurrency, fxRates),
    };
  });

  entityGroups.sort(function (a, b) {
    return a.entityName.localeCompare(b.entityName);
  });

  if (unassignedAccounts.length > 0) {
    entityGroups.push({
      entityId: 'unassigned',
      entityName: 'Unassigned',
      subtotal: computeGroupSubtotal(unassignedAccounts, displayCurrency, fxRates),
      banks: groupAccountsByBank(unassignedAccounts, displayCurrency, fxRates),
    });
  }

  return entityGroups;
}
